// Partner cockpit dashboard. A landing with KPI cards derived from the shared-engagements
// query + quick links. Tenant switcher + richer mandate overview land with B3-PARTNER-COCKPIT
// wiring (C4.x). PLAN-C1.11. Rebuilt onto shadcn (W34 PATH-B).
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Handshake, CircleCheck, Clock } from "lucide-react";
import { useSession } from "../lib/session";
import { Can } from "../lib/rbac";
import { api } from "../lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

function isConfirmed(row: Record<string, unknown>): boolean {
  const status = String(row.status ?? row.engagementStatus ?? "");
  return status === "confirmed" || status === "active";
}

function Kpi({ icon, label, value, loading }: { icon: React.ReactNode; label: string; value: number; loading?: boolean }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <span className="text-muted-foreground">{icon}</span>
      </CardHeader>
      <CardContent>
        {loading ? <Skeleton className="h-8 w-12" /> : <div className="text-3xl font-semibold tracking-tight">{value}</div>}
      </CardContent>
    </Card>
  );
}

export function DashboardRoute() {
  const session = useSession();
  const canReadEngagements = true; // KPI query is harmless; affordances gate on permission below.

  const { data, isLoading } = useQuery({
    queryKey: ["shared-engagements"],
    queryFn: () => api.listSharedEngagements(),
    enabled: canReadEngagements,
  });
  const rows = (data ?? []) as Array<Record<string, unknown>>;
  const total = rows.length;
  const confirmed = rows.filter(isConfirmed).length;
  const pending = total - confirmed;

  return (
    <div>
      <PageHeader
        title="Partner cockpit"
        description={`${session.user.email} · firm consultant`}
      />

      <Can perm="partner_relationship.read">
        <div className="grid gap-4 sm:grid-cols-3">
          <Kpi icon={<Handshake className="size-4" />} label="Shared engagements" value={total} loading={isLoading} />
          <Kpi icon={<CircleCheck className="size-4" />} label="Confirmed" value={confirmed} loading={isLoading} />
          <Kpi icon={<Clock className="size-4" />} label="Pending confirmation" value={pending} loading={isLoading} />
        </div>
      </Can>

      <h2 className="mt-8 mb-3 text-sm font-semibold text-muted-foreground">Quick links</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <Can perm="partner_relationship.read">
          <Link to="/engagements" className="block">
            <Card className="transition-colors hover:border-primary/40 hover:bg-accent/40">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  Engagements
                  <ArrowRight className="size-4 text-muted-foreground" />
                </CardTitle>
                <CardDescription>
                  Cross-tenant processor engagements you have a mandate on. Confirm pending engagements (C6.0).
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>
        </Can>
      </div>
    </div>
  );
}
